import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../../i18n';

const arcadeConfig = [
  {
    id: 'jobs',
    number: '01',
    available: true,
    arcade: {
      src: 'https://demo.arcade.software/i3NwX53PfAbmgoUb8HGw?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
      title: "Feed with Jobs demo",
    },
  },
  {
    id: 'sourcing',
    number: '02',
    available: true,
    arcade: {
      src: 'https://demo.arcade.software/WJj9x4p7Og2nJGyKpf28?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
      title: "Sourcing demo",
    },
  },
  {
    id: 'screening',
    number: '03',
    available: true,
    arcade: {
      src: 'https://demo.arcade.software/9zXkhqgMfVds2TUknitd?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
      title: "Screening demo",
    },
  },
  {
    id: 'interview',
    number: '04',
    available: false,
    arcade: null,
  },
  {
    id: 'testcase',
    number: '05',
    available: false,
    arcade: null,
  },
];

type StepWithTranslation = typeof arcadeConfig[0] & {
  label: string;
  you?: string;
  ai?: string;
};

const ComingSoonPanel: React.FC<{ step: StepWithTranslation; comingSoon: string }> = ({ step, comingSoon }) => (
  <div className="w-full h-full flex flex-col items-center justify-center px-10">
    <div className="flex flex-col items-center gap-8 text-center max-w-sm">
      <div className="flex items-center gap-4">
        <div
          className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <span className="text-lg">👤</span>
          <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Vous</span>
        </div>
        <span className="text-white/20 text-lg font-light">+</span>
        <div
          className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(40,230,117,0.06)', border: '1px solid rgba(40,230,117,0.15)' }}
        >
          <span className="text-lg">✦</span>
          <span className="text-[10px] font-medium text-[#28e675]/60 uppercase tracking-wider">IA</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 text-left">
        <p className="text-xs text-white/40 leading-relaxed">
          <span className="text-white/60 font-medium">Vous — </span>{step.you}
        </p>
        <p className="text-xs text-white/40 leading-relaxed">
          <span className="text-[#28e675]/70 font-medium">IA — </span>{step.ai}
        </p>
      </div>
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-white/25" />
        {comingSoon}
      </div>
    </div>
  </div>
);

export const ArcadeDemo: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useI18n();

  const steps: StepWithTranslation[] = arcadeConfig.map((cfg) => {
    const s = t.pipeline.steps[cfg.id as keyof typeof t.pipeline.steps];
    return {
      ...cfg,
      label: s.label,
      you: 'you' in s ? s.you : undefined,
      ai: 'ai' in s ? s.ai : undefined,
    };
  });

  const step = steps[activeStep];

  return (
    <section className="relative py-24 bg-black">
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <p className="text-[#28e675] text-sm font-medium tracking-widest uppercase mb-3">
            {t.pipeline.label}
          </p>
          <h2 className="text-white font-display text-3xl sm:text-4xl md:text-5xl leading-tight mb-4">
            {t.pipeline.title}
            <br />
            <span className="text-white/40">{t.pipeline.titleMuted}</span>
          </h2>
          <p className="text-white/35 text-base max-w-xl mx-auto leading-relaxed">
            {t.pipeline.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Tab switcher */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {steps.map((s, index) => {
              const isActive = activeStep === index;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveStep(index)}
                  className="relative flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300"
                  style={{
                    background: isActive ? 'rgba(40,230,117,0.1)' : 'rgba(255,255,255,0.04)',
                    border: isActive ? '1px solid rgba(40,230,117,0.3)' : '1px solid rgba(255,255,255,0.08)',
                    color: isActive ? '#28e675' : 'rgba(255,255,255,0.4)',
                  }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTabBg"
                      className="absolute inset-0 rounded-full"
                      style={{ background: 'rgba(40,230,117,0.08)' }}
                      transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                    />
                  )}
                  <span className="relative z-10 font-mono text-[10px] opacity-50">{s.number}</span>
                  <span className="relative z-10">{s.label}</span>
                  {!s.available && (
                    <span
                      className="relative z-10 text-[9px] font-medium px-1.5 py-0.5 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)' }}
                    >
                      {t.pipeline.comingSoon}
                    </span>
                  )}
                </button>
              );
            })}
          </div>


          {/* Demo panel */}
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.07)',
              height: '620px',
            }}
          >
            <AnimatePresence exitBeforeEnter>
              <motion.div
                key={activeStep}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {step.arcade ? (
                  <iframe
                    src={step.arcade.src}
                    title={step.arcade.title}
                    frameBorder="0"
                    loading="lazy"
                    allowFullScreen
                    allow="clipboard-write"
                    style={{ width: '100%', height: '100%', colorScheme: 'light' }}
                  />
                ) : (
                  <ComingSoonPanel step={step} comingSoon={t.pipeline.comingSoon} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>


        </motion.div>
      </div>
    </section>
  );
};
